import React, { useCallback, useMemo } from "react";
import { TextField, Checkbox, FormControlLabel, MenuItem } from "@mui/material";
import styles from "./FilterBar.module.scss";
import { AutocompleteField } from "../components/AutocompleteField";

export const FilterBar = ({
	columnFilters,
	columns,
	globalFilters,
	onFilterChange,
	title = null,
}) => {
	const handleFilterChange = useCallback(
		(columnId, value) => {
			onFilterChange(columnId, value);
		},
		[onFilterChange],
	);

	const visibleFilters = useMemo(() => {
		return columns
			.filter(
				(column) =>
					column.meta?.filter && column.meta.filter.showInFilterBar !== false,
			)
			.map((column) => ({
				id: column.accessorKey,
				...column.meta.filter,
			}));
	}, [columns]);

	const getCurrentFilterValue = useCallback(
		(filterId) => {
			const filter = columnFilters?.find((f) => f.id === filterId);
			return filter ? filter.value : null;
		},
		[columnFilters],
	);

	const renderFilterField = (filter) => {
		const currentValue = getCurrentFilterValue(filter.key || filter.id);

		switch (filter.variant) {
			case "date":
				return (
					<TextField
						type="date"
						label={filter.label}
						value={currentValue || ""}
						onChange={(e) =>
							handleFilterChange(
								filter.key || filter.id,
								e.target.value === "" ? null : e.target.value,
							)
						}
						size="small"
						InputLabelProps={{ shrink: true }}
						sx={{ zIndex: 0 }}
					/>
				);
			case "date-range": {
				const [startDate, endDate] = (currentValue || "").split("|");
				return (
					<div className={styles.dateRangeContainer}>
						<TextField
							type="date"
							label={filter.labels[0]}
							value={startDate || null}
							onChange={(e) => {
								const start = e.target.value;
								handleFilterChange(
									filter.key || filter.id,
									start === "" && !endDate ? null : `${start}|${endDate || ""}`,
								);
							}}
							size="small"
							InputLabelProps={{ shrink: true }}
							sx={{ zIndex: 0 }}
						/>
						<TextField
							type="date"
							label={filter.labels[1]}
							value={endDate || null}
							onChange={(e) => {
								const end = e.target.value;
								handleFilterChange(
									filter.key || filter.id,
									end === "" && !startDate ? null : `${startDate || ""}|${end}`,
								);
							}}
							size="small"
							InputLabelProps={{ shrink: true }}
							sx={{ zIndex: 0 }}
						/>
					</div>
				);
			}
			case "boolean":
				return (
					<FormControlLabel
						sx={{ zIndex: 0 }}
						control={
							<Checkbox
								checked={currentValue || false}
								onChange={(e) =>
									handleFilterChange(filter.key || filter.id, e.target.checked)
								}
							/>
						}
						label={filter.label}
					/>
				);
			case "select":
				return (
					<TextField
						label={filter.label}
						value={currentValue}
						select
						SelectProps={{ autoWidth: true }}
						onChange={(e) => handleFilterChange(filter.key || filter.id, e.target.value)}
						size="small"
						sx={{ minWidth: "10em", zIndex: 0 }}
					>
						{filter.options.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
				);
			case "async-select": {
				return (
					<AutocompleteField
						value={currentValue}
						label={filter.label}
						onChange={(newValue) =>
							handleFilterChange(filter.key || filter.id, newValue)
						}
						resource={filter.resource}
						filterKey={filter.key}
						perPage={filter.perPage}
						filters={{ ...(filter.filters || {}), ...(globalFilters || {}) }}
						groupBy={filter.groupBy}
						sort={filter.sort}
						distinct={filter.distinct}
						include={filter.include}
						getOptionLabel={filter.getOptionLabel}
						getOptionValue={filter.getOptionValue}
						isOptionEqualToValue={filter.isOptionEqualToValue}
					/>
				);
			}
			default:
				return (
					<TextField
						sx={{ zIndex: 0 }}
						label={filter.label}
						value={currentValue || ""}
						onChange={(e) =>
							handleFilterChange(
								filter.key || filter.id,
								e.target.value === "" ? null : e.target.value,
							)
						}
						size="small"
					/>
				);
		}
	};

	if (visibleFilters.length === 0) return null;

	return (
		<div className={styles.container}>
			{title && <div>{title}</div>}
			<div className={styles.body}>
				{visibleFilters.map((filter) => (
					<div key={filter.id} className={styles.filter}>
						{renderFilterField(filter)}
					</div>
				))}
			</div>
		</div>
	);
};
